import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
  type UseQueryOptions,
} from '@tanstack/react-query';

import { apiGet, apiPath, apiPost } from '@/lib/api/client';
import { CubixApiError } from '@/lib/api/types';

import type { FtpAnalysisDetail, TriggerFtpRequest } from './types';

const ftpKey = (date: string | undefined) =>
  ['alm', 'ftp', 'latest', date] as const;

/**
 * GET /api/v1/alm/ftp?date=YYYY-MM-DD
 *
 * Returns the persisted FTP result for the given tenant + date, or null
 * if no calculation has run for that date yet.
 *
 * When `date` is undefined the server returns the most recent result.
 */
export function useLatestFtp(
  date: string | undefined,
  options?: Omit<
    UseQueryOptions<FtpAnalysisDetail | null, CubixApiError>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery({
    queryKey: ftpKey(date),
    enabled: Boolean(date),
    queryFn: async () => {
      const url = `${apiPath('alm', 'ftp')}?date=${encodeURIComponent(date!)}`;
      const data = await apiGet<FtpAnalysisDetail | null>(url);
      return data ?? null;
    },
    ...options,
  });
}

/**
 * POST /api/v1/alm/ftp/calculate — triggers an on-demand FTP calculation.
 * Server returns 202 Accepted with the FtpAnalysisDetail.
 */
export function useTriggerFtp(
  options?: UseMutationOptions<
    FtpAnalysisDetail,
    CubixApiError,
    TriggerFtpRequest
  >,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      apiPost<FtpAnalysisDetail, TriggerFtpRequest>(
        apiPath('alm', 'ftp', 'calculate'),
        body,
      ),
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: ['alm', 'ftp'] });
      return options?.onSuccess?.(...args);
    },
  });
}
